import { isValidLinkedInUrl } from "@/components/utils/validation/validateLinkedInURL";
import { yupRequiredString } from "@/components/yup-utils";
import { yupPhoneNumber } from "@/flokit/utils/validation/enum/yup-phone-number";
import { isNil } from "lodash";
import * as luxon from "luxon";
import {
  JobAppPersonalInformationRequest,
  JobAppQuestionAnswerRequest,
  JobAppQuestionAnswerSelectionRequest,
  JobAppSelfIdQuestionAnswerRequest,
} from "modules/api-docs";
import { QuestionType } from "modules/graphql.types";
import * as Yup from "yup";

export type DocumentFields = {
  orgFriendlyName?: string;
  jobExtension?: string;
  jobAppId?: number;
  orgcustomdocumentsId: number;
  file?: Blob;
  isFinalStep?: boolean;
  createdAt?: string;
};

// educationHistoryRequired: boolean;
export type EducationHistoryFields = {
  university: string | null;
  degree: string | null;
  endDate: string | null;
};

export const educationHistoryYupSchema = Yup.object({
  university: yupRequiredString().nullable(),
  degree: yupRequiredString().nullable(),
  endDate: yupRequiredString().nullable(),
});

export type WorkHistoryFields = {
  employer: string | null;
  position: string | null;
  startDate: string | null;
  endDate?: string | null;
  isCurrentEmployer: boolean;
};

export const workHistoryYupSchema = Yup.object({
  employer: yupRequiredString(),
  position: yupRequiredString(),
  startDate: yupRequiredString(),
  endDate: Yup.string()
    .nullable()
    .when(["isCurrentEmployer"], {
      is: (v: boolean) => Boolean(!v),
      then: Yup.string()
        .required()
        .test(
          "endDateIsValid",
          "End date must be on or after start date",
          (_, context: any) => {
            try {
              const endDate = luxon.DateTime.fromISO(context.parent.endDate);
              const startDate = luxon.DateTime.fromISO(
                context.parent.startDate,
              );
              return endDate >= startDate;
            } catch (e) {
              return false;
            }
          },
        ),
    }),
  isCurrentEmployer: Yup.boolean().required(),
});

export const educationHistoryYup = Yup.array()
  .of(
    Yup.object({
      university: yupRequiredString(),
      degree: yupRequiredString(),
      endDate: yupRequiredString(),
    }),
  )
  .when("educationHistoryRequired", {
    is: true,
    then: Yup.array().min(1),
  })
  .default([]);

export const workHistoryYup = Yup.array()
  .of(
    Yup.object({
      employer: yupRequiredString(),
      position: yupRequiredString(),
      startDate: yupRequiredString(),
      // Any end date will be valid for current employer
      endDate: Yup.string()
        .nullable()
        .when(["isCurrentEmployer"], {
          is: (v: unknown) => Boolean(!v),
          then: Yup.string()
            .required()
            .test(
              "endDateIsValid",
              "End date must be on or after start date",
              (_, context: any) => {
                try {
                  const endDate = luxon.DateTime.fromISO(
                    context.parent.endDate,
                  );
                  const startDate = luxon.DateTime.fromISO(
                    context.parent.startDate,
                  );
                  return endDate >= startDate;
                } catch (e) {
                  return false;
                }
              },
            ),
        }),
      isCurrentEmployer: Yup.boolean(),
    }),
  )
  .when("workHistoryRequired", {
    is: true,
    then: Yup.array().min(1),
  })
  .default([]);

export const addressInfoRequiredYup = Yup.object({
  addressLineOne: yupRequiredString(),
  addressLineTwo: Yup.string().optional(),
  city: yupRequiredString(),
  country: yupRequiredString()
    // Request object takes 'undefined', not 'null', so we can't use '.nullable()'. But select dropdown uses 'null' for empty option. If we don't transform, then 'yup' will complain that 'null' is not valid (because we're passing in 'null')
    .transform((a) => (isNil(a) ? undefined : a)),
  zipCode: yupRequiredString(),
  state: yupRequiredString(),
}).required();

export const addressInfoOptionalYup = Yup.object({
  addressLineOne: Yup.string().optional(),
  addressLineTwo: Yup.string().optional(),
  city: Yup.string().optional(),
  country: Yup.string()
    .optional()
    .transform((a) => (isNil(a) ? undefined : a)),
  zipCode: Yup.string().optional(),
  state: Yup.string().optional(),
}).optional();

const customQuestionAnswerSelectionsSchema: Yup.SchemaOf<JobAppQuestionAnswerSelectionRequest> =
  Yup.object({
    id: Yup.number().optional(),
    orgQuestionOptionId: Yup.number().required(),
    jobAppQuestionAnswerId: Yup.number().optional(),
  });

export type SelfApplyCustomQuestionAnswer = JobAppQuestionAnswerRequest & {
  isRequired: boolean;
  questionType?: string;
};

export const customQuestionsYupSchema: Yup.SchemaOf<SelfApplyCustomQuestionAnswer> =
  Yup.object({
    id: Yup.number().optional(),
    isRequired: Yup.boolean().required(),
    jobAppId: Yup.number().optional(),
    orgQuestionId: Yup.number().required(),
    questionType: Yup.string().required(),
    dateValue: Yup.string().when(["questionType", "isRequired"], {
      is: (questionType: QuestionType, isRequired: boolean) => {
        return questionType === "DATE" && isRequired;
      },
      then: yupRequiredString(),
      otherwise: Yup.string().optional().nullable(),
    }),
    numericValue: Yup.number()
      .typeError("Please type a valid number.")
      .when(["questionType", "isRequired"], {
        is: (questionType: QuestionType, isRequired: boolean) => {
          return questionType === "NUMERICAL" && isRequired;
        },
        then: Yup.number()
          .typeError("Please type a valid number")
          .required("Please type a valid number."),
        otherwise: Yup.number().optional(),
      }),
    textValue: Yup.string().when(["questionType", "isRequired"], {
      is: (questionType: QuestionType, isRequired: boolean) => {
        return questionType === "FREE_TEXT" && isRequired;
      },
      then: yupRequiredString("Please answer this question."),
      otherwise: Yup.string().optional().nullable(),
    }),

    // This is the property that is not typing correctly
    jobAppQuestionAnswerSelections: Yup.array()
      .of(customQuestionAnswerSelectionsSchema)
      .optional()
      .when(["questionType", "isRequired"], {
        is: (questionType: QuestionType, isRequired: boolean) => {
          const isSelectQuestion =
            questionType === QuestionType.SingleSelect ||
            questionType === QuestionType.MultiSelect;

          if (isSelectQuestion && isRequired) {
            return true;
          }
          return false;
        },
        then: Yup.array()
          .of(customQuestionAnswerSelectionsSchema)
          .min(1, "This field is required"),
        otherwise: Yup.array()
          .of(customQuestionAnswerSelectionsSchema)
          .optional(),
      }),
  });

export const candidateSelfIdQuestionAnswerYup: Yup.SchemaOf<JobAppSelfIdQuestionAnswerRequest> =
  Yup.object({
    id: Yup.number().optional(),
    text: Yup.string().optional(),
    orgSelfIdQuestionId: Yup.number().required(),
    jobAppSelfIdQuestionAnswerSelections: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().optional(),
          orgSelfIdQuestionOptionId: Yup.number().required(),
        }),
      )
      .min(1, "Please select an answer.")
      .required("Please select an answer."),
  });

export const CandidateSelfApplyPersonalInfoSchema: Yup.SchemaOf<JobAppPersonalInformationRequest> =
  Yup.object({
    firstName: yupRequiredString(),
    lastName: yupRequiredString(),
    email: Yup.string().email().required(),
    phoneNumber: Yup.string().when({
      is: (v: unknown) => {
        const isBlank = typeof v === "string" && !v.trim();
        return !isBlank && v !== null && v !== undefined;
      },
      then: yupPhoneNumber(),
    }),

    linkedIn: Yup.string().when({
      is: (v: unknown) => {
        const isBlank = typeof v === "string" && !v.trim();
        return !isBlank && v !== null && v !== undefined;
      },
      then: Yup.string().test(
        "validateLinkedInURL",
        "Invalid LinkedIn URL",
        function (v) {
          // Shouldn't happen due to 'is' clause above
          if (!v) {
            return true;
          }

          return isValidLinkedInUrl(v);
        },
      ),
    }),
  }).required();

export const CandidateSelfApplyAddressInfoSchema = Yup.object().when(
  "addressInfoRequired",
  {
    is: (addressInfoRequired: boolean) => addressInfoRequired,
    then: addressInfoRequiredYup,
    otherwise: addressInfoOptionalYup,
  },
);

export const selfApplyDocumentUploadYupSchema: Yup.SchemaOf<DocumentFields> =
  Yup.object({
    orgFriendlyName: yupRequiredString(),
    jobExtension: yupRequiredString(),
    file: Yup.mixed().optional(),
    jobAppId: Yup.number().optional(),
    orgcustomdocumentsId: Yup.number().required(),
    isFinalStep: Yup.boolean(),
    createdAt: Yup.string().optional(),
  });

export const CandidateSelfApplyJobApplicationSchema = Yup.object({
  personalInfo: CandidateSelfApplyPersonalInfoSchema,
  addressInfoRequired: Yup.boolean(),
  workHistoryRequired: Yup.boolean(),
  educationHistoryRequired: Yup.boolean(),
  addressInfo: CandidateSelfApplyAddressInfoSchema,
  selfIdQuestionsRequired: Yup.boolean(),
  // should be enabled if selfIdQuestionsRequired is true
  selfIdQuestionAnswers: Yup.array().when("selfIdQuestionsRequired", {
    is: true,
    then: Yup.array().of(candidateSelfIdQuestionAnswerYup).min(1),
    otherwise: Yup.array().optional().nullable(),
  }),
  workHistory: workHistoryYup,
  educationHistory: educationHistoryYup,
  applicationQuestions: Yup.array().of(customQuestionsYupSchema),
  jobExtension: yupRequiredString(),
  orgFriendlyName: yupRequiredString(),
  isFinalStep: Yup.boolean(),
});
